<template>
  <div class="form-edit-page">
    <div class="page-header">
      <h1>Cadastro de Itens de Processo</h1>
      <p>Checklist parametrizável por tipo de processo no SIM</p>
    </div>

    <div class="page-content two-columns">
      <!-- COLUNA ESQUERDA -->
      <section class="panel panel-list">
        <div class="panel-header">
          <h2>Tipos de Processo</h2>
        </div>

        <div class="process-list">
          <div
            v-for="processo in lsProcessos"
            :key="processo.id"
            class="process-card"
            :class="{ active: processoSelecionado.id === processo.id }"
            @click="selecionarProcesso(processo)"
          >
            <div class="process-card-top">
              <strong>{{ processo.descricao }}</strong>
              <span class="badge">{{ processo.categoria }}</span>
            </div>
            <div class="process-card-bottom">
              <span>Status: {{ processo.ativo ? 'Ativo' : 'Inativo' }}</span>
            </div>
          </div>
        </div>

        <div class="panel-header mt-20">
          <h2>Checklist do Processo</h2>
        </div>

        <div v-if="processoSelecionado.id" class="table-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 60px;">Ord.</th>
                <th>Descrição</th>
                <th style="width: 130px;">Tipo</th>
                <th style="width: 90px;">Obrig.</th>
                <th style="width: 90px;">Anexo</th>
                <th style="width: 90px;">Obs.</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in itensDoProcesso" :key="item.id">
                <td>{{ item.ordem }}</td>
                <td>{{ item.descricao }}</td>
                <td>{{ item.tipoItem }}</td>
                <td>{{ item.obrigatorio ? 'Sim' : 'Não' }}</td>
                <td>{{ item.exigeAnexo ? 'Sim' : 'Não' }}</td>
                <td>{{ item.exigeObservacao ? 'Sim' : 'Não' }}</td>
              </tr>
              <tr v-if="!itensDoProcesso.length">
                <td colspan="6" class="empty-row">
                  Nenhum item cadastrado para este processo.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-box">
          Selecione um tipo de processo para visualizar o checklist.
        </div>
      </section>

      <!-- COLUNA DIREITA -->
      <section class="panel panel-form">
        <div class="panel-header">
          <h2>Novo Item do Processo</h2>
        </div>

        <div class="form-body">
          <div class="form-group">
            <label>Processo</label>
            <input
              type="text"
              :value="processoSelecionado.descricao || ''"
              readonly
              placeholder="Selecione um processo na lista ao lado"
            />
          </div>

          <div class="row">
            <div class="form-group col-2">
              <label>Ordem</label>
              <input type="number" v-model="formItem.ordem" min="1" />
            </div>

            <div class="form-group col-4">
              <label>Tipo do Item</label>
              <select v-model="formItem.tipoItem">
                <option value="">Selecione</option>
                <option value="Documento">Documento</option>
                <option value="Etapa">Etapa</option>
                <option value="Verificação técnica">Verificação técnica</option>
                <option value="Declaração">Declaração</option>
                <option value="Anexo obrigatório">Anexo obrigatório</option>
                <option value="Parecer">Parecer</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label>Descrição do Item</label>
            <input
              type="text"
              v-model="formItem.descricao"
              placeholder="Ex.: Memorial descritivo da estrutura"
            />
          </div>

          <div class="form-group">
            <label>Orientação Técnica</label>
            <textarea
              v-model="formItem.orientacaoTecnica"
              rows="4"
              placeholder="Informe instruções, critérios de conferência ou observações para o item..."
            ></textarea>
          </div>

          <div class="row checks">
            <label class="check-item">
              <input type="checkbox" v-model="formItem.obrigatorio" />
              <span>Item obrigatório</span>
            </label>

            <label class="check-item">
              <input type="checkbox" v-model="formItem.exigeAnexo" />
              <span>Exige anexo</span>
            </label>

            <label class="check-item">
              <input type="checkbox" v-model="formItem.exigeObservacao" />
              <span>Exige observação</span>
            </label>

            <label class="check-item">
              <input type="checkbox" v-model="formItem.ativo" />
              <span>Ativo</span>
            </label>
          </div>

          <div class="form-actions">
            <button class="btn btn-primary" @click="adicionarItem">
              Adicionar item à lista
            </button>
            <button class="btn btn-secondary" @click="limparFormulario">
              Limpar
            </button>
          </div>

          <div class="note-box">
            <strong>Conceito da POC:</strong>
            <p>
              Este cadastro permite parametrizar os itens exigidos em cada processo
              do SIM. O processo "Registro de Estabelecimento" é apenas um dos
              cenários possíveis.
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';

const lsProcessos = ref([
  {
    id: 1,
    descricao: 'Registro de Estabelecimento no SIM',
    categoria: 'Registro',
    ativo: true,
  },
  {
    id: 2,
    descricao: 'Renovação de Registro',
    categoria: 'Renovação',
    ativo: true,
  },
  {
    id: 3,
    descricao: 'Alteração Cadastral do Estabelecimento',
    categoria: 'Alteração',
    ativo: true,
  },
  {
    id: 4,
    descricao: 'Vistoria Prévia de Funcionamento',
    categoria: 'Vistoria',
    ativo: true,
  },
]);

const lsItens = ref([
  {
    id: 1,
    idProcesso: 1,
    ordem: 1,
    descricao: 'Requerimento formal de registro',
    tipoItem: 'Documento',
    obrigatorio: true,
    exigeAnexo: false,
    exigeObservacao: false,
    ativo: true,
    orientacaoTecnica: 'Conferir preenchimento e assinatura do responsável.',
  },
  {
    id: 2,
    idProcesso: 1,
    ordem: 2,
    descricao: 'CNPJ ou CPF do responsável',
    tipoItem: 'Documento',
    obrigatorio: true,
    exigeAnexo: true,
    exigeObservacao: false,
    ativo: true,
    orientacaoTecnica: 'Validar compatibilidade com o cadastro do estabelecimento.',
  },
  {
    id: 3,
    idProcesso: 1,
    ordem: 3,
    descricao: 'Memorial descritivo da estrutura',
    tipoItem: 'Documento',
    obrigatorio: true,
    exigeAnexo: true,
    exigeObservacao: true,
    ativo: true,
    orientacaoTecnica: 'Verificar adequação conforme atividade pretendida.',
  },
  {
    id: 4,
    idProcesso: 1,
    ordem: 4,
    descricao: 'Planta baixa / layout da unidade',
    tipoItem: 'Anexo obrigatório',
    obrigatorio: true,
    exigeAnexo: true,
    exigeObservacao: false,
    ativo: true,
    orientacaoTecnica: 'Avaliar fluxo de produção e segregação de áreas.',
  },
  {
    id: 5,
    idProcesso: 1,
    ordem: 5,
    descricao: 'Laudo de potabilidade da água',
    tipoItem: 'Documento',
    obrigatorio: true,
    exigeAnexo: true,
    exigeObservacao: false,
    ativo: true,
    orientacaoTecnica: 'Confirmar validade do documento apresentado.',
  },
  {
    id: 6,
    idProcesso: 2,
    ordem: 1,
    descricao: 'Comprovante de manutenção das condições do registro',
    tipoItem: 'Declaração',
    obrigatorio: true,
    exigeAnexo: false,
    exigeObservacao: true,
    ativo: true,
    orientacaoTecnica: 'Verificar se houve alterações estruturais ou operacionais.',
  },
]);

const processoSelecionado = ref({});

const formItem = reactive({
  ordem: 1,
  descricao: '',
  tipoItem: '',
  obrigatorio: true,
  exigeAnexo: false,
  exigeObservacao: false,
  ativo: true,
  orientacaoTecnica: '',
});

const itensDoProcesso = computed(() => {
  if (!processoSelecionado.value.id) return [];
  return lsItens.value
    .filter(item => item.idProcesso === processoSelecionado.value.id)
    .sort((a, b) => a.ordem - b.ordem);
});

function selecionarProcesso(processo) {
  processoSelecionado.value = { ...processo };

  const proximaOrdem =
    itensDoProcesso.value.length > 0
      ? Math.max(...itensDoProcesso.value.map(a => a.ordem)) + 1
      : 1;

  formItem.ordem = proximaOrdem;
}

function limparFormulario() {
  formItem.ordem = itensDoProcesso.value.length
    ? Math.max(...itensDoProcesso.value.map(a => a.ordem)) + 1
    : 1;
  formItem.descricao = '';
  formItem.tipoItem = '';
  formItem.obrigatorio = true;
  formItem.exigeAnexo = false;
  formItem.exigeObservacao = false;
  formItem.ativo = true;
  formItem.orientacaoTecnica = '';
}

function adicionarItem() {
  if (!processoSelecionado.value.id) {
    alert('Selecione um processo antes de adicionar itens.');
    return;
  }

  if (!formItem.descricao.trim()) {
    alert('Informe a descrição do item.');
    return;
  }

  if (!formItem.tipoItem) {
    alert('Selecione o tipo do item.');
    return;
  }

  lsItens.value.push({
    id: Date.now(),
    idProcesso: processoSelecionado.value.id,
    ordem: Number(formItem.ordem || 0),
    descricao: formItem.descricao,
    tipoItem: formItem.tipoItem,
    obrigatorio: formItem.obrigatorio,
    exigeAnexo: formItem.exigeAnexo,
    exigeObservacao: formItem.exigeObservacao,
    ativo: formItem.ativo,
    orientacaoTecnica: formItem.orientacaoTecnica,
  });

  limparFormulario();
}
</script>

<style scoped>
.form-edit-page {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 26px;
  color: #243447;
}

.page-header p {
  margin: 6px 0 0;
  color: #5c6b77;
}

.page-content.two-columns {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 20px;
}

.panel {
  background: #fff;
  border: 1px solid #dbe3ea;
  border-radius: 10px;
  overflow: hidden;
}

.panel-header {
  padding: 14px 16px;
  border-bottom: 1px solid #e7edf2;
  background: #f8fafc;
}

.panel-header h2 {
  margin: 0;
  font-size: 18px;
  color: #243447;
}

.panel-form .form-body {
  padding: 16px;
}

.process-list {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.process-card {
  border: 1px solid #d8e1e8;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: 0.2s;
  background: #fff;
}

.process-card:hover {
  border-color: #7aa7c7;
  background: #f8fbfd;
}

.process-card.active {
  border-color: #2d7dbb;
  background: #eef6fc;
}

.process-card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.process-card-bottom {
  margin-top: 8px;
  color: #667784;
  font-size: 13px;
}

.badge {
  background: #dfeef9;
  color: #2d6f9f;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

.table-wrapper {
  padding: 0 16px 16px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  border-bottom: 1px solid #e8edf2;
  padding: 10px 8px;
  text-align: left;
  font-size: 14px;
}

.data-table th {
  background: #f8fafc;
  color: #41505c;
}

.empty-row,
.empty-box {
  text-align: center;
  color: #6c7a86;
  padding: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 600;
  color: #34424e;
}

.form-group input,
.form-group select,
.form-group textarea {
  border: 1px solid #cfd8df;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #5c9ed6;
}

.row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.col-2 {
  grid-column: span 2;
}

.col-4 {
  grid-column: span 4;
}

.checks {
  margin: 10px 0 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.check-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #41505c;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.btn {
  border: 0;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 600;
}

.btn-primary {
  background: #2d7dbb;
  color: #fff;
}

.btn-secondary {
  background: #e7edf2;
  color: #34424e;
}

.note-box {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid #d8e6f2;
  border-radius: 8px;
  background: #f4f9fd;
  color: #44525e;
}

.mt-20 {
  margin-top: 20px;
}

@media (max-width: 1100px) {
  .page-content.two-columns {
    grid-template-columns: 1fr;
  }

  .row {
    grid-template-columns: 1fr;
  }

  .col-2,
  .col-4 {
    grid-column: span 1;
  }
}
</style>